import React from "react";
import { Box, Typography, Chip, Stack, Avatar } from "@mui/joy";
import { FileStack, BadgeCheck, AlertTriangle, LucideIcon } from "lucide-react";
import { useDashboardData } from "../hooks";

type StatColor = "primary" | "success" | "warning";

interface StatCardProps {
    label: string;
    count: number;
    trendValue: string;
    trendLabel: string;
    color: StatColor;
    icon: LucideIcon;
}

const StatCard = ({ label, count, trendValue, trendLabel, color, icon: Icon }: StatCardProps) => (
    <Box
        sx={{
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 2,
            height: "100%",
            flex: 1,
            borderRight: "1px solid",
            borderColor: "divider",
            "&:last-of-type": { borderRight: "none" },
        }}
    >
        <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
                variant="soft"
                color={color}
                sx={{
                    "--Avatar-size": "44px",
                    bgcolor: `${color}.softBg`,
                    color: `${color}.500`,
                    borderRadius: "lg",
                    border: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Icon size={20} />
            </Avatar>
            <Box>
                <Typography level="title-md" gutterBottom>
                    {label} : {count}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                    <Chip
                        size="md"
                        variant="soft"
                        color={color}
                        sx={{
                            borderRadius: "xs",
                            px: 0.8,
                        }}
                    >
                        {trendValue}
                    </Chip>
                    <Typography level="body-sm">{trendLabel}</Typography>
                </Stack>
            </Box>
        </Stack>
    </Box>
);

export default function TopStats() {
    const { stats } = useDashboardData();

    const statConfigs = [
        {
            label: "Running",
            count: stats.running,
            trendValue: "Active",
            trendLabel: "Live projects",
            color: "primary" as const,
            icon: FileStack,
        },
        {
            label: "Completed",
            count: stats.completed,
            trendValue: `${stats.completed > 0 ? "+" : ""}${stats.completed}`,
            trendLabel: "Success items",
            color: "success" as const,
            icon: BadgeCheck,
        },
        {
            label: "At Risk",
            count: stats.atRisk,
            trendValue: "Critical",
            trendLabel: "Passed ETA",
            color: "warning" as const,
            icon: AlertTriangle,
        },
    ];

    return (
        <Box sx={{ display: "flex", height: "100%", width: "100%", flex: 1 }}>
            {statConfigs.map((stat, i) => (
                <StatCard key={i} {...stat} />
            ))}
        </Box>
    );
}
